import apiManagerUser from 'api/manager-users';
import { useDialog } from 'components/DialogV3';
import DialogAcceptPolicy from 'components/DialogV3/DialogAcceptPolicy';
import LanguageCC from 'components/Layout/LanguageCC';
import { PATH_ROOT, URL_LANDING_PAGE } from 'constants/api';
import useLoginSSO from 'hooks/useLoginSSO';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { establishSession } from 'store/sessionSlice';
import { validateDomain, toastError, toastSuccess } from 'utils/helpers';
import LoginSSOSystem from './components/LoginSSOSystem';
import LoginSSOWith2FA from './components/LoginSSOWith2FA';
import { AppInfo, FooterAuthentication } from 'layouts';
import useSafeParams from 'hooks/useSafeParams';
import { TOAST_DEFAULT } from 'constants/toast';
import { useAuthFlow } from '../AuthFlowContext';

const withoutCredentials = (data = {}) => {
    const {
        accessToken,
        refreshToken,
        access_token,
        refresh_token,
        vtc_refresh_token,
        temp_token,
        ...safeInfo
    } = data;

    return safeInfo;
};

export default function Callback() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const [search] = useSearchParams();
    const { loginSSOSuccess } = useLoginSSO();
    const { getProvider, isReady: isProviderReady } = useSafeParams();
    const provider = getProvider(params.provider);
    // const state = search?.get('state')
    const hash = window.location.hash.substring(1);

    // Chuyển thành URLSearchParams để dễ parse
    const _params = new URLSearchParams(hash);

    const accessToken = _params.get('access_token');
    const code = search?.get('code') || accessToken;
    const error = search?.get('error');
    const redirect_uri = search?.get('redirect_uri');
    const organization_id = search?.get('organization_id');

    const [errorMessage, setErrorMessage] = useState(error);
    const [enable2FA, setEnable2FA] = useState(false);

    const dispatch = useDispatch();
    const { sso, updateSSOFlow, clearSSOFlow } = useAuthFlow();
    const SSOInfo = sso.info || {};
    const { isProvideService } = SSOInfo;
    const [needAcceptShareInfo, setNeedAcceptShareInfo] = useState(false);
    const { openDialog } = useDialog();
    const [refreshToken, setRefreshToken] = useState();

    useEffect(() => {
        if (!accessToken && !search.get('code')) return;

        const sanitized = new URL(window.location.href);
        sanitized.hash = '';
        sanitized.searchParams.delete('code');
        sanitized.searchParams.delete('session_state');
        window.history.replaceState(null, '', `${sanitized.pathname}${sanitized.search}`);
        // Credential values captured for this render remain available to the exchange below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verifyGoogleSSO = (data, shareInfo, grantType = '') => {
        return apiManagerUser.verifyGoogleSSO(data, provider, undefined, shareInfo, grantType);
    };

    const handleRedirectUri = (uri) => {
        const url = window.location.href;
        const cleanedUrl = url.replace(/(&?session_state=[^&]*|&?code=[^&]*)/g, '').replace(/[?&]+$/, '');
        if (cleanedUrl.includes('sme')) {
            return cleanedUrl;
        } else {
            return uri;
        }
    };

    useEffect(() => {
        if (errorMessage) {
            clearSSOFlow();
            setTimeout(() => {
                navigate(`${PATH_ROOT}/auth`);
            }, 3000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorMessage]);

    useEffect(() => {
        if (isProviderReady && !provider) {
            setErrorMessage('auth.sth-ww');
        }
    }, [isProviderReady, provider]);

    useEffect(() => {
        if (needAcceptShareInfo) {
            openDialog(DialogAcceptPolicy, {
                onAction: async (policies) => {
                    const response = await verifyGoogleSSO(
                        {
                            access_token: provider === 'viettel-cloud' ? (refreshToken ? refreshToken : code) : code,
                            code_verifier: SSOInfo.code_verifier,
                            redirect_uri: handleRedirectUri(redirect_uri || SSOInfo.redirect_uri),
                        },
                        policies,
                        'refresh_token',
                    );

                    if (response.data?.type !== 'Login' || response.data?.error === 'PI_SHARE_NOT_ALLOW') {
                        toastError(t(TOAST_DEFAULT.ERROR));
                        return;
                    }

                    toastSuccess(t('toast.loginSuccess'));
                    if (response.data?.message === 'AUTH_2FA_ENABLED') {
                        setEnable2FA(true);
                        updateSSOFlow({ tempToken: response.data?.temp_token });
                    } else {
                        loginSSOSuccess(response?.data, isProvideService);
                        clearSSOFlow();
                    }
                },
                onReject: () => {
                    clearSSOFlow();
                    toastError(t('toast.error.title.loginError'), t('toast.error.content.sharingInfo'), {
                        autoClose: 5000,
                    });
                    navigate(`${PATH_ROOT}/login`);
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needAcceptShareInfo]);

    useEffect(() => {
        if (code && isProviderReady && provider) {
            const fetchData = async () => {
                try {
                    updateSSOFlow({ accessToken: code, info: SSOInfo });
                    const response = await verifyGoogleSSO({
                        access_token: code,
                        code_verifier: SSOInfo.code_verifier,
                        redirect_uri: handleRedirectUri(redirect_uri || SSOInfo.redirect_uri),
                    });

                    if (response.data?.type === 'Login') {
                        if (response.data?.error === 'PI_SHARE_NOT_ALLOW') {
                            setRefreshToken(response.data?.refresh_token || '');
                            setNeedAcceptShareInfo(true);
                        } else if (response.data?.message === 'AUTH_2FA_ENABLED') {
                            setEnable2FA(true);
                            updateSSOFlow({ tempToken: response.data?.temp_token });
                        } else {
                            toastSuccess(t('toast.loginSuccess'));
                            const { accessToken, refreshToken } = response?.data;
                            dispatch(establishSession({ accessToken, refreshToken }));
                            clearSSOFlow();

                            const domain = validateDomain(localStorage.getItem('redirectDomain'));

                            if (domain) {
                                window.location.href = domain;
                                localStorage.removeItem('redirectDomain');
                            } else {
                                navigate(`${PATH_ROOT || '/'}${isProvideService ? '?provide-service=true' : ''}`);
                            }
                        }
                    } else {
                        if (response?.data?.vtc_refresh_token) {
                            updateSSOFlow({
                                accessToken: response?.data?.vtc_refresh_token,
                                info: {
                                    ...SSOInfo,
                                    ...withoutCredentials(response?.data),
                                    isProvideService,
                                    grant_type: 'refresh_token',
                                    organization_id: organization_id,
                                },
                            });
                        } else {
                            updateSSOFlow({
                                info: {
                                    ...SSOInfo,
                                    ...withoutCredentials(response?.data),
                                    isProvideService,
                                    grant_type: 'authorization_code',
                                    organization_id: organization_id,
                                },
                            });
                        }
                        navigate(`${PATH_ROOT}/register/verifySSO/` + provider);
                    }
                } catch (e) {
                    clearSSOFlow();
                    if (e?.response?.status === 401) {
                        setErrorMessage(e.response.data.message);
                    } else {
                        setErrorMessage('auth.sth-ww');
                    }
                }
            };

            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code, isProviderReady, provider]);

    return (
        <div className="register-email-container">
            <div className="drop-down-language">
                <LanguageCC />
            </div>
            <div
                style={{ position: 'relative', width: '100%', cursor: 'pointer' }}
                onClick={() => window.open(URL_LANDING_PAGE)}
            >
                <img
                    alt="logo-register-img"
                    style={{ position: 'absolute', marginBottom: '0px' }}
                    className="logo-register-img"
                    src={require('../../../assets/img/logo_020823.svg').default}
                ></img>
            </div>
            <div style={{ width: '100%' }}>
                <div className="register-email-body">
                    <div className="register-email-image">
                        <div className="register-email-body-left">
                            <AppInfo />
                            <img
                                alt="link-account"
                                className=""
                                width="90%"
                                src={require('../../../assets/img/link_account.svg').default}
                            ></img>
                        </div>
                    </div>
                    <div className="register-email-dialog">
                        {enable2FA ? (
                            <LoginSSOWith2FA isProvideService={isProvideService} />
                        ) : (
                            <LoginSSOSystem provider={provider} errorMessage={errorMessage} />
                        )}
                    </div>
                </div>
            </div>
            <FooterAuthentication />
        </div>
    );
}
